import React, { useState, useEffect, useCallback } from 'react';
import { Input, Tooltip, message } from 'antd';
import { useGraphicStandFields } from 'components/use-hooks';
import useGraphicStandMutationCreateUpdate from '../hooks/graphic-stand-use-mutation-create-update';
import './graphic-stand-comment-field.scss';

const { TextArea } = Input;

const GraphicStandCommentField = ({ graphicStand, field = 'comment' }) => {
    const { getFieldValue, setFieldValue } = useGraphicStandFields(graphicStand?.id);
    const [value, setValue] = useState('');
    const [isEditing, setIsEditing] = useState(false);

    const { update } = useGraphicStandMutationCreateUpdate({
        graphicStandId: graphicStand?.id,
        skipRedirect: true,
        skipNotifications: true
    });

    // Load initial value
    useEffect(() => {
        const initialValue = graphicStand?.[field] || getFieldValue(field) || '';
        setValue(initialValue);
    }, [graphicStand, field, getFieldValue]);

    // Update cache when value changes
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setFieldValue(field, value);
        }, 100);
        return () => clearTimeout(timeoutId);
    }, [value, field, setFieldValue]);

    const handleChange = useCallback((e) => {
        setValue(e.target.value);
    }, []);

                const handleBlur = useCallback(async () => {
        setIsEditing(false);

        // Always save on blur to allow empty values
        const originalValue = graphicStand?.[field] || '';
        if (value !== originalValue) {
            // Send all required fields for update
            const input = {
                id: graphicStand.id,
                exhibitor: graphicStand.exhibitor,
                stand_id: graphicStand.stand_id,
                hall_name: graphicStand.hall_name || '',
                corridor: graphicStand.corridor || '',
                block: graphicStand.block || '',
                stand_number: graphicStand.stand_number || '',
                graphic_list_id: graphicStand.graphic_list_id,
                status: graphicStand.status || 'done',
                [field]: value
            };

            try {
                await update({
                    variables: {
                        input
                    }
                });
            } catch (error) {
                // Revert value on error and show error message
                setValue(originalValue);
                message.error(`Error updating ${field}: ${error.message}`);
            }
        }
    }, [value, field, graphicStand, update]);

    const handleFocus = useCallback(() => {
        setIsEditing(true);
    }, []);

    const handleClick = useCallback(() => {
        setIsEditing(true);
    }, []);

    if (!graphicStand) {
        return <strong>&mdash;</strong>;
    }

    const shouldShowInput = isEditing || value;
    const displayValue = value || graphicStand?.[field] || '';

    if (shouldShowInput) {
        return (
            <Tooltip title={displayValue} placement="topLeft">
                <TextArea
                    className="comment-field-textarea"
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    autoFocus={isEditing && !value}
                    rows={2}
                    placeholder="Enter comment..."
                />
            </Tooltip>
        );
    }

    return (
        <div
            className="field-placeholder"
            onClick={handleClick}
            style={{ cursor: 'pointer', minHeight: '50px', display: 'flex', alignItems: 'center' }}
        >
            <strong>&mdash;</strong>
        </div>
    );
};

export default GraphicStandCommentField;
